import { useTranslation } from "@sagot/i18n";
import { Avatar, Badge, Button, Dialog, DialogContent, EmptyState, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useBuildFormatters } from "../shared/format";
import { useMemberNames, useSettingsMutations, useVersions } from "./use-agent-settings";

interface VersionsDialogProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  canPublish: boolean;
  onRestored: () => void;
}

/** `history-modal`: published versions with Current badge, Restore, and Publish current settings. */
export function VersionsDialog({ agentId, open, onOpenChange, canEdit, canPublish, onRestored }: VersionsDialogProps) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const message = useErrorMessage();
  const versions = useVersions(agentId, open);
  const nameOf = useMemberNames(open);
  const { restore, publish } = useSettingsMutations(agentId);
  const fail = (error: unknown) => { toast.error(message(error)); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t("build.agentSettings.versions.title")}
        description={t("build.agentSettings.versions.hint")}
        closeLabel={t("common.close")}
        footer={canPublish ? (
          <Button loading={publish.isPending} onClick={() => { publish.mutate(undefined, { onSuccess: (v) => { toast.success(t("build.agentSettings.versions.published", { version: v.version })); }, onError: fail }); }}>
            <Icon name="play" />
            {t("build.agentSettings.versions.publish")}
          </Button>
        ) : undefined}
      >
        {versions.isPending ? <div className="space-y-3" aria-hidden="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div> : null}
        {versions.isError ? <ErrorState title={t("build.loadError")} onRetry={() => void versions.refetch()} retryLabel={t("common.retry")} /> : null}
        {versions.data?.length === 0 ? <EmptyState icon="history" headingLevel={3} title={t("build.agentSettings.versions.empty")} description={t("build.agentSettings.versions.emptyBody")} /> : null}
        <ul className="-mx-5 max-h-[50vh] divide-y divide-line overflow-y-auto">
          {versions.data?.map((version) => {
            const author = nameOf(version.created_by) ?? t("build.agentSettings.versions.someone");
            return (
              <li key={version.version} className="flex items-center gap-3 px-5 py-3">
                <Avatar name={author} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{version.summary ?? t("build.agentSettings.versions.version", { version: version.version })}</div>
                  <div className="text-xs text-fg-subtle">{t("build.agentSettings.versions.by", { user: author, time: fmt.dateTime(version.published_at ?? version.created_at) })}</div>
                </div>
                {version.is_current ? (
                  <Badge tone="success">{t("build.agentSettings.versions.current")}</Badge>
                ) : canEdit ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={restore.isPending && restore.variables === version.version}
                    onClick={() => {
                      restore.mutate(version.version, {
                        onSuccess: () => { toast.success(t("build.agentSettings.versions.restored", { version: version.version })); onRestored(); onOpenChange(false); },
                        onError: fail,
                      });
                    }}
                  >
                    {t("build.agentSettings.versions.restore")}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
